import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Zap,
  RefreshCw,
  Search,
  X,
  Plus,
  User,
  Bot,
  Shield,
  Hash,
  Eye,
  Clock,
  ChevronDown,
  ChevronRight,
  Play,
  Archive,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Skill, skillsApi } from '@/services/api/agentApi';
import * as errorUtils from '@/utils/errors';

// ─── Status badge colors ─────────────────────────────────────────────────────

export const statusStyles: Record<string, string> = {
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  canary: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  deprecated: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export const domainStyles: Record<string, string> = {
  data: 'bg-blue-500/20 text-blue-400',
  workflow: 'bg-purple-500/20 text-purple-400',
  general: 'bg-muted text-muted-foreground',
};

function isStatusFilter(value: string): value is StatusFilter {
  return value === 'all' || value === 'draft' || value === 'active' || value === 'canary' || value === 'deprecated';
}

function isDomainFilter(value: string): value is DomainFilter {
  return value === 'all' || value === 'data' || value === 'workflow' || value === 'general';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const OriginIcon: React.FC<{ origin: string }> = ({ origin }) => {
  switch (origin) {
    case 'human': return <User className="h-3 w-3" />;
    case 'system': return <Shield className="h-3 w-3" />;
    default: return <Bot className="h-3 w-3" />;
  }
};

export const SuccessRateBar: React.FC<{ rate: number }> = ({ rate }) => {
  const color = rate > 0.7 ? '#22c55e' : rate >= 0.4 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${rate * 100}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs w-10 text-right font-medium" style={{ color }}>
        {(rate * 100).toFixed(0)}%
      </span>
    </div>
  );
};

export const timeAgo = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

// ─── Skill Card ───────────────────────────────────────────────────────────────

export const SkillCard: React.FC<{
  skill: Skill;
  onActivate: (id: string) => void;
  onDeprecate: (id: string) => void;
  activating: string | null;
  /** When provided, renders an "Insert" button for executing the skill in the editor chat. */
  onInsert?: () => void;
}> = ({ skill, onActivate, onDeprecate, activating, onInsert }) => {
  const [expanded, setExpanded] = useState(false);
  const currentVersion = skill.versions.find(v => v.version === skill.current_version);

  return (
    <div className="border border-border rounded-lg bg-card hover:border-primary/40 transition-colors">
      <div className="p-4 space-y-3">
        {/* Header: name + badges */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-sm truncate">{skill.name}</h4>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {skill.description}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant="outline" className={statusStyles[skill.status] || ''}>
              {skill.status}
            </Badge>
          </div>
        </div>

        {/* Domain + Origin badges */}
        <div className="flex flex-wrap items-center gap-1.5">
          {skill.domain && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${domainStyles[skill.domain] || domainStyles.general}`}>
              {skill.domain}
            </span>
          )}
          <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">
            <OriginIcon origin={skill.origin} />
            {skill.origin}
          </span>
          <span className="text-[10px] text-muted-foreground">v{skill.current_version}</span>
        </div>

        {/* Success rate */}
        <SuccessRateBar rate={skill.success_rate} />

        {/* Tags */}
        {skill.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {skill.tags.map((tag, i) => (
              <span key={i} className="flex items-center gap-0.5 text-xs px-2 py-0.5 rounded bg-secondary">
                <Hash className="h-2.5 w-2.5" />
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Eye className="h-3 w-3" />
            {skill.use_count}x used
          </span>
          {skill.last_used_at && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {timeAgo(skill.last_used_at)}
            </span>
          )}
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-primary hover:underline"
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {expanded ? 'Hide details' : 'Show details'}
        </button>

        {/* Expanded: instructions + version history + triggers */}
        {expanded && (
          <div className="space-y-3 pt-2 border-t border-border">
            {currentVersion && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Instructions (v{currentVersion.version})</p>
                <pre className="text-xs bg-muted/50 rounded p-3 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
                  {currentVersion.instructions}
                </pre>
              </div>
            )}
            {skill.versions.length > 1 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Version history</p>
                <div className="space-y-1">
                  {skill.versions.map((v, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={`font-mono ${v.version === skill.current_version ? 'font-bold text-primary' : 'text-muted-foreground'}`}>
                        v{v.version}
                      </span>
                      <span className="text-muted-foreground">{new Date(v.created_at).toLocaleDateString()}</span>
                      {v.changelog && <span className="text-muted-foreground truncate">- {v.changelog}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {skill.triggers.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Triggers</p>
                <div className="space-y-1">
                  {skill.triggers.map((t, i) => (
                    <div key={i} className="text-xs bg-secondary rounded px-2 py-1">
                      <span className="font-medium">{t.type}</span>
                      {t.config && Object.keys(t.config).length > 0 && (
                        <span className="text-muted-foreground ml-1">
                          ({Object.entries(t.config).map(([k, v]) => `${k}: ${v}`).join(', ')})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-border flex items-center gap-2">
        {onInsert && skill.status === 'active' && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-primary border-primary/40 hover:bg-primary/10"
            onClick={onInsert}
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            Insert
          </Button>
        )}
        {(skill.status === 'draft' || skill.status === 'canary') && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onActivate(skill.skill_id)}
            disabled={activating === skill.skill_id}
          >
            {activating === skill.skill_id ? (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Play className="w-3.5 h-3.5 mr-1.5" />
            )}
            Activate
          </Button>
        )}
        {skill.status === 'active' && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-muted-foreground hover:text-destructive"
            onClick={() => onDeprecate(skill.skill_id)}
            disabled={activating === skill.skill_id}
          >
            {activating === skill.skill_id ? (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Archive className="w-3.5 h-3.5 mr-1.5" />
            )}
            Deprecate
          </Button>
        )}
      </div>
    </div>
  );
};

// ─── Create Skill Dialog ──────────────────────────────────────────────────────

export const CreateSkillDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}> = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [domain, setDomain] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName(''); setDescription(''); setInstructions('');
    setDomain(''); setTagsInput(''); setError(null);
  };

  const handleCreate = async () => {
    if (!name.trim() || !description.trim() || !instructions.trim()) {
      setError('Name, description, and instructions are required.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
      await skillsApi.create({
        name: name.trim(),
        description: description.trim(),
        instructions: instructions.trim(),
        domain: domain || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err: unknown) {
      setError(errorUtils.getApiErrorMessage(err, 'Failed to create skill'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent variant="light" className="sm:max-w-lg p-0 gap-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <Plus className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-card-foreground">Create skill</h2>
            <p className="text-[11px] text-muted-foreground">Define a reusable instruction set for agents</p>
          </div>
        </div>

        <div className="border-t border-border px-4 py-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-card-foreground">Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Weekly Revenue Report"
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-card-foreground">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Short description of what this skill does..."
              rows={2}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-card-foreground">Instructions</label>
            <textarea
              value={instructions}
              onChange={e => setInstructions(e.target.value)}
              placeholder="Step-by-step instructions for the agent to follow..."
              rows={6}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none font-mono"
            />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-card-foreground">Domain</label>
              <select
                value={domain}
                onChange={e => setDomain(e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground"
              >
                <option value="">General</option>
                <option value="data">Data</option>
                <option value="workflow">Workflow</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-card-foreground">Tags</label>
              <input
                type="text"
                value={tagsInput}
                onChange={e => setTagsInput(e.target.value)}
                placeholder="comma, separated"
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-border bg-muted/30">
          <Button variant="outline" size="sm" onClick={() => { reset(); onOpenChange(false); }} className="border-border text-card-foreground hover:bg-muted">Cancel</Button>
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating && <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Create skill
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main SkillsTab ───────────────────────────────────────────────────────────

export type StatusFilter = 'all' | 'draft' | 'active' | 'canary' | 'deprecated';
export type DomainFilter = 'all' | 'data' | 'workflow' | 'general';

export const SkillsTab: React.FC = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [activating, setActivating] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (domainFilter !== 'all') params.domain = domainFilter;
      const result = await skillsApi.list(params);
      const data = result?.data;
      setSkills(data?.skills || []);
      setTotal(data?.total || 0);
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to load skills'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, domainFilter]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const handleActivate = async (skillId: string) => {
    setActivating(skillId);
    try {
      await skillsApi.activate(skillId);
      fetchSkills();
    } catch { /* silent */ } finally {
      setActivating(null);
    }
  };

  const handleDeprecate = async (skillId: string) => {
    setActivating(skillId);
    try {
      await skillsApi.deprecate(skillId);
      fetchSkills();
    } catch { /* silent */ } finally {
      setActivating(null);
    }
  };

  // Client-side search filter
  const filtered = useMemo(() => {
    if (!searchInput.trim()) return skills;
    const q = searchInput.toLowerCase();
    return skills.filter(
      s => s.name.toLowerCase().includes(q)
        || s.description.toLowerCase().includes(q)
        || s.tags.some(t => t.toLowerCase().includes(q)),
    );
  }, [skills, searchInput]);

  // Summary stats
  const stats = useMemo(() => {
    const active = skills.filter(s => s.status === 'active').length;
    const draft = skills.filter(s => s.status === 'draft').length;
    const agentProposed = skills.filter(s => s.origin === 'agent').length;
    return { active, draft, agentProposed };
  }, [skills]);

  if (loading && skills.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!loading && skills.length === 0 && !error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-8">
        <Zap className="w-16 h-16 mb-4 opacity-40" />
        <h3 className="text-lg font-medium text-foreground mb-1">No skills yet</h3>
        <p className="text-sm text-center mb-6 max-w-md">
          Skills are reusable instructions that agents follow for specific tasks.
          Create one manually or let agents propose them from successful workflows.
        </p>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-1.5" />
          Create skill
        </Button>
        <CreateSkillDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={fetchSkills} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary" />
          <span className="font-medium">{total} skill{total !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Create
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchSkills} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border flex-shrink-0">
        {/* Status filter */}
        <select
          className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
          value={statusFilter}
          onChange={(e) => {
            if (isStatusFilter(e.target.value)) {
              setStatusFilter(e.target.value);
            }
          }}
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="canary">Canary</option>
          <option value="deprecated">Deprecated</option>
        </select>

        {/* Domain filter */}
        <select
          className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
          value={domainFilter}
          onChange={(e) => {
            if (isDomainFilter(e.target.value)) {
              setDomainFilter(e.target.value);
            }
          }}
        >
          <option value="all">All domains</option>
          <option value="data">Data</option>
          <option value="workflow">Workflow</option>
          <option value="general">General</option>
        </select>

        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, description, or tag..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-8 py-1.5 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Summary stats row */}
      <div className="flex items-center gap-4 px-4 py-2.5 bg-muted/30 border-b border-border text-xs flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-muted-foreground">Active:</span>
          <span className="font-medium">{stats.active}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-slate-400" />
          <span className="text-muted-foreground">Draft:</span>
          <span className="font-medium">{stats.draft}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Bot className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">Agent-proposed:</span>
          <span className="font-medium">{stats.agentProposed}</span>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm flex-shrink-0">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Skill cards */}
      <ScrollArea className="flex-1">
        <div className="p-4 grid gap-4 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {filtered.map(skill => (
            <SkillCard
              key={skill.skill_id}
              skill={skill}
              onActivate={handleActivate}
              onDeprecate={handleDeprecate}
              activating={activating}
            />
          ))}
        </div>
        {filtered.length === 0 && skills.length > 0 && (
          <div className="text-center text-sm text-muted-foreground py-8">
            No skills match your search.
          </div>
        )}
      </ScrollArea>

      <CreateSkillDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={fetchSkills} />
    </div>
  );
};
